const publishArticleGuideEvent = (source, action, detail = {}) => {
  const eventDetail = { action, ...detail };

  if (source && typeof CustomEvent === 'function') {
    source.dispatchEvent(
      new CustomEvent('nw:article-commerce', {
        bubbles: true,
        detail: eventDetail,
      }),
    );
  }

  if (typeof window !== 'undefined') {
    window.dataLayer?.push?.({
      event: 'nw_article_guide',
      ...eventDetail,
    });
  }
};

if (!customElements.get('article-guide-product-card')) {
  customElements.define(
    'article-guide-product-card',
    class ArticleGuideProductCard extends HTMLElement {
      connectedCallback() {
        if (this.isConnectedToGuide) return;

        this.variantSelect = this.querySelector('[data-article-guide-variant-select]');
        this.price = this.querySelector('[data-article-guide-price]');
        this.image = this.querySelector('[data-article-guide-image]');
        this.addButton = this.querySelector('[data-article-guide-add]');
        this.productLinks = [...this.querySelectorAll('[data-article-guide-product-link]')];
        this.productForm = this.querySelector('form');
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onVariantKeyDown = this.onVariantKeyDown.bind(this);
        this.onProductLinkClick = this.onProductLinkClick.bind(this);
        this.onAddClick = this.onAddClick.bind(this);
        this.onAddIntent = this.onAddIntent.bind(this);
        this.onAddConfirmed = this.onAddConfirmed.bind(this);

        this.variantSelect?.addEventListener('change', this.onVariantChange);
        this.variantSelect?.addEventListener('keydown', this.onVariantKeyDown);
        this.productLinks.forEach((link) => link.addEventListener('click', this.onProductLinkClick));
        this.addButton?.addEventListener('click', this.onAddClick);
        this.productForm?.addEventListener('submit', this.onAddIntent);
        this.eventDocument = this.ownerDocument || (typeof document !== 'undefined' ? document : null);
        this.eventDocument?.addEventListener('nw:cart-add-confirmed', this.onAddConfirmed);
        if (this.addButton && typeof MutationObserver === 'function') {
          this.busyObserver = new MutationObserver(() => {
            const busy = this.addButton.classList.contains('loading');
            if (this.variantSelect) this.variantSelect.disabled = busy;
            if (!busy && this.selectedOption) this.addButton.disabled = this.selectedOption.dataset.available === 'false';
          });
          this.busyObserver.observe(this.addButton, { attributes: true, attributeFilter: ['class'] });
        }
        this.isConnectedToGuide = true;
      }

      disconnectedCallback() {
        this.variantSelect?.removeEventListener('change', this.onVariantChange);
        this.variantSelect?.removeEventListener('keydown', this.onVariantKeyDown);
        this.productLinks?.forEach((link) => link.removeEventListener('click', this.onProductLinkClick));
        this.addButton?.removeEventListener('click', this.onAddClick);
        this.productForm?.removeEventListener('submit', this.onAddIntent);
        this.eventDocument?.removeEventListener('nw:cart-add-confirmed', this.onAddConfirmed);
        this.busyObserver?.disconnect();
        this.pendingVariantId = '';
        this.isConnectedToGuide = false;
      }

      get selectedOption() {
        return this.variantSelect?.selectedOptions?.[0] || null;
      }

      get eventDetail() {
        return {
          articleHandle: this.dataset.articleHandle || '',
          productHandle: this.dataset.productHandle || '',
          productId: this.dataset.productId || '',
          variantId: this.selectedOption?.value || this.querySelector('[name="id"]')?.value || '',
          merchandisingPosition: Number(this.dataset.merchandisingPosition || 0),
        };
      }

      emitGuideEvent(action, detail = {}) {
        publishArticleGuideEvent(this, action, { ...this.eventDetail, ...detail });
      }

      onVariantChange(optionOverride) {
        const option = optionOverride?.dataset ? optionOverride : this.selectedOption;
        if (!option) return;

        if (this.price && option.dataset.price) this.price.textContent = option.dataset.price;
        if (option.dataset.url) this.productLinks.forEach((link) => link.setAttribute('href', option.dataset.url));
        const available = option.dataset.available !== 'false' && !option.disabled;
        if (this.addButton && !this.addButton.classList?.contains('loading')) {
          this.addButton.disabled = !available;
          const label = this.addButton.querySelector('[data-product-submit-label]');
          if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
        }
        const stockNote = this.querySelector('[data-article-guide-stock-note]');
        if (stockNote) stockNote.hidden = available;

        if (this.image && option.dataset.imageSrc) {
          this.image.src = option.dataset.imageSrc;
          if (option.dataset.imageSrcset) this.image.srcset = option.dataset.imageSrcset;
          if (option.dataset.imageAlt) this.image.alt = option.dataset.imageAlt;
        }

        this.emitGuideEvent('variant_change');
      }

      selectVariantBySize(size) {
        if (!this.variantSelect || !size) return false;

        const normalizedSize = String(size).trim().toLowerCase();
        const options = [...(this.variantSelect.options || [])];
        const currentColor = this.selectedOption?.dataset.optionOne || '';
        const matchingOptions = options.filter((option) => (
          String(option.dataset.optionTwo || '').trim().toLowerCase().startsWith(normalizedSize)
        ));
        const availableOptions = matchingOptions.filter((option) => option.dataset.available !== 'false' && !option.disabled);
        const choices = availableOptions.length ? availableOptions : matchingOptions;
        const target = choices.find((option) => option.dataset.optionOne === currentColor) || choices[0];
        if (!target) return false;

        this.variantSelect.value = target.value;
        if ('selectedIndex' in this.variantSelect) this.variantSelect.selectedIndex = options.indexOf(target);
        this.onVariantChange(target);
        return true;
      }

      onVariantKeyDown(event) {
        if (event.key === 'Enter') event.preventDefault();
      }

      onProductLinkClick() {
        this.emitGuideEvent('product_click');
      }

      onAddClick() {
        if (!this.productForm || this.addButton?.disabled || this.addButton?.classList?.contains('loading')) return;
        if (typeof this.productForm.requestSubmit === 'function') this.productForm.requestSubmit();
      }

      onAddIntent() {
        this.pendingVariantId = String(this.eventDetail.variantId || '');
        this.emitGuideEvent('add_intent');
      }

      onAddConfirmed(event) {
        const confirmedVariantId = String(event?.detail?.variantId || '');
        if (!this.pendingVariantId || confirmedVariantId !== this.pendingVariantId) return;

        this.emitGuideEvent('add_confirmed', { variantId: confirmedVariantId });
        this.pendingVariantId = '';
      }
    },
  );
}

if (!customElements.get('article-guide-fit-checker')) {
  customElements.define(
    'article-guide-fit-checker',
    class ArticleGuideFitChecker extends HTMLElement {
      connectedCallback() {
        if (this.isConnectedToGuide) return;

        this.inputs = [...this.querySelectorAll('[data-article-guide-fit-input]')];
        this.unitRadios = [...this.querySelectorAll('input[type="radio"]')];
        this.unitLabels = [...this.querySelectorAll('[data-article-guide-unit-label]')];
        this.results = [...this.querySelectorAll('[data-article-guide-fit-result]')];
        this.publishedInteriors = [...this.querySelectorAll('[data-article-guide-published-interior]')];
        this.roundingNote = this.querySelector('[data-article-guide-rounding-note]');
        this.sizeActions = [...this.querySelectorAll('[data-article-guide-size-action]')];
        this.fieldErrors = new Map(
          [...this.querySelectorAll('[data-article-guide-fit-error]')]
            .map((error) => [error.dataset.articleGuideFitError, error]),
        );
        this.status = this.querySelector('[data-article-guide-fit-status]');
        this.resultGrid = this.querySelector('[data-article-guide-fit-result-grid]');
        this.resultArea = this.querySelector('[data-article-guide-fit-results]');
        this.summaryDetail = this.querySelector('[data-article-guide-fit-summary-detail]');
        this.comparison = this.querySelector('[data-article-guide-fit-comparison]');
        this.primaryAction = this.sizeActions[0];
        this.checkButton = this.querySelector('[data-article-guide-fit-check]');
        this.measureVisual = this.querySelector('[data-article-guide-measure-visual]');
        this.measureChoices = [...this.querySelectorAll('[data-article-guide-measure-choice]')];
        this.previewChoices = [...this.querySelectorAll('[data-article-guide-preview-size]')];
        this.comparisonVisual = this.querySelector('[data-article-guide-comparison-visual]');
        this.dimensionRows = [...this.querySelectorAll('[data-article-guide-dimension-row]')];
        this.activeMeasurement = 'width';
        this.previewSize = '';
        this.hasChecked = false;
        this.validationRequested = false;
        this.clearButton = this.querySelector('[data-article-guide-fit-clear]');
        this.measurementsInches = new Map();
        this.unit = this.unitRadios.find((radio) => radio.checked)?.value || 'in';
        this.onInput = this.onInput.bind(this);
        this.onCheck = this.onCheck.bind(this);
        this.onMeasureFocus = this.onMeasureFocus.bind(this);
        this.onMeasureChoice = this.onMeasureChoice.bind(this);
        this.onPreviewChoice = this.onPreviewChoice.bind(this);
        this.onMeasurementKeyDown = this.onMeasurementKeyDown.bind(this);
        this.onUnitChange = this.onUnitChange.bind(this);
        this.onClear = this.onClear.bind(this);
        this.onSizeAction = this.onSizeAction.bind(this);

        this.inputs.forEach((input) => input.addEventListener('input', this.onInput));
        this.inputs.forEach((input) => input.addEventListener('focus', this.onMeasureFocus));
        this.measureChoices.forEach((button) => button.addEventListener('click', this.onMeasureChoice));
        this.previewChoices.forEach((button) => button.addEventListener('click', this.onPreviewChoice));
        this.inputs.forEach((input) => input.addEventListener('keydown', this.onMeasurementKeyDown));
        this.unitRadios.forEach((radio) => radio.addEventListener('change', this.onUnitChange));
        this.clearButton?.addEventListener('click', this.onClear);
        this.checkButton?.addEventListener('click', this.onCheck);
        this.sizeActions.forEach((action) => action.addEventListener('click', this.onSizeAction));
        this.updateDimensionLabels();
        this.updateResults();
        this.isConnectedToGuide = true;
      }

      disconnectedCallback() {
        this.inputs?.forEach((input) => input.removeEventListener('input', this.onInput));
        this.inputs?.forEach((input) => input.removeEventListener('focus', this.onMeasureFocus));
        this.measureChoices?.forEach((button) => button.removeEventListener('click', this.onMeasureChoice));
        this.previewChoices?.forEach((button) => button.removeEventListener('click', this.onPreviewChoice));
        this.inputs?.forEach((input) => input.removeEventListener('keydown', this.onMeasurementKeyDown));
        this.unitRadios?.forEach((radio) => radio.removeEventListener('change', this.onUnitChange));
        this.clearButton?.removeEventListener('click', this.onClear);
        this.checkButton?.removeEventListener('click', this.onCheck);
        this.sizeActions?.forEach((action) => action.removeEventListener('click', this.onSizeAction));
        if (this.fitEventTimer && typeof clearTimeout === 'function') clearTimeout(this.fitEventTimer);
        this.fitEventTimer = null;
        this.isConnectedToGuide = false;
      }

      inchesToUnit(value, unit = this.unit) {
        return unit === 'cm' ? value * 2.54 : value;
      }

      unitToInches(value, unit = this.unit) {
        return unit === 'cm' ? value / 2.54 : value;
      }

      formatMeasurement(value, unit = this.unit) {
        const converted = this.inchesToUnit(value, unit);
        if (unit === 'in') return converted.toFixed(2);
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(converted.toFixed(2)));
      }

      formatClearance(value) {
        const converted = this.inchesToUnit(value);
        if (converted > 0 && converted < 0.01) return '<0.01';
        return this.formatMeasurement(value);
      }

      updateDimensionLabels() {
        const unitLabel = this.unit === 'cm' ? 'cm' : 'in';
        this.unitLabels.forEach((label) => { label.textContent = unitLabel; });
        if (this.roundingNote) this.roundingNote.hidden = this.unit !== 'cm';
        [...this.results, ...this.publishedInteriors].forEach((result) => {
          const dimensions = ['widthIn', 'depthIn', 'thicknessIn'].map((key) => Number(result.dataset[key]));
          const label = result.querySelector('[data-article-guide-dimensions]');
          if (!label || dimensions.some((value) => !Number.isFinite(value))) return;
          const approximation = this.unit === 'cm' ? '≈ ' : '';
          label.textContent = `${approximation}${dimensions.map((value) => this.formatMeasurement(value)).join(' × ')} ${unitLabel}${result.hasAttribute?.('data-article-guide-published-interior') ? '' : ' interior'}`;
        });
      }

      onUnitChange(event) {
        if (!event.target.checked || event.target.value === this.unit) return;

        const previousUnit = this.unit;
        const nextUnit = event.target.value;
        this.inputs.forEach((input) => {
          const value = Number(input.value);
          if (!Number.isFinite(value) || value <= 0) return;
          const stored = this.measurementsInches.get(input);
          const inches = stored?.display === input.value && stored.unit === previousUnit
            ? stored.inches : this.unitToInches(value, previousUnit);
          input.value = String(Number(this.inchesToUnit(inches, nextUnit).toFixed(6)));
          this.measurementsInches.set(input, { inches, display: input.value, unit: nextUnit });
        });
        this.unit = nextUnit;
        this.updateDimensionLabels();
        this.updateResults();
      }

      onInput(event) {
        if (event?.target) this.measurementsInches.delete(event.target);
        this.hasChecked = false;
        this.updateResults();
      }

      onCheck(event) {
        event?.preventDefault?.();
        this.validationRequested = true;
        this.hasChecked = true;
        this.previewSize = '';
        this.updateResults();
        const invalid = this.inputs.find((input) => input.validity?.badInput || !Number.isFinite(Number(input.value)) || Number(input.value) <= 0);
        (invalid || this.status)?.focus();
      }

      onMeasureFocus(event) {
        this.activeMeasurement = event.currentTarget.dataset.articleGuideFitInput;
        this.updateMeasurementVisual();
      }

      onMeasureChoice(event) {
        const dimension = event.currentTarget.dataset.articleGuideMeasureChoice;
        const input = this.inputs.find((field) => field.dataset.articleGuideFitInput === dimension);
        if (!input) return;
        this.activeMeasurement = dimension;
        this.updateMeasurementVisual();
        input.focus({ preventScroll: true });
      }

      onPreviewChoice(event) {
        const size = event.currentTarget.dataset.articleGuidePreviewSize;
        if (!this.hasChecked || !this.results.some((result) => result.dataset.variantSizeMatch === size)) return;
        this.previewSize = size;
        this.updateResults();
      }

      displayDimension(inches) {
        const value = this.inchesToUnit(inches);
        return Math.abs(value) >= 100000 ? value.toExponential(2) : new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(value);
      }

      updateMeasurementVisual() {
        if (!this.measureVisual) return;
        const dimension = this.activeMeasurement || 'width';
        const label = `${dimension[0].toUpperCase()}${dimension.slice(1)}`;
        const input = this.inputs.find((field) => field.dataset.articleGuideFitInput === dimension);
        const valid = input && !input.validity?.badInput && Number.isFinite(Number(input.value)) && Number(input.value) > 0;
        this.measureVisual.dataset.measurement = dimension;
        this.measureChoices.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.articleGuideMeasureChoice === dimension)));
        const valueLabel = this.measureVisual.querySelector('[data-article-guide-measure-value]');
        if (valueLabel) valueLabel.textContent = `${label} · ${valid ? `${this.displayDimension(this.unitToInches(Number(input.value)))} ${this.unit}` : 'enter your measurement'}`;
        this.measureVisual.querySelector('[data-article-guide-measure-image]')?.setAttribute('aria-label', `${label} measurement of a closed laptop, including its case`);
        const hints = {
          width: 'Measure the widest edge, from left to right.',
          depth: 'Measure from the front edge to the back, including the hinge.',
          thickness: 'Measure the thickest point with the laptop closed, including feet and case.',
        };
        const hint = this.measureVisual.querySelector('[data-article-guide-measure-hint]');
        if (hint) hint.textContent = hints[dimension];
      }

      comparisonGeometry(measurements, limits) {
        const widest = Math.max(measurements[0], ...this.results.map((result) => Number(result.dataset.widthIn)));
        const deepest = Math.max(measurements[1], ...this.results.map((result) => Number(result.dataset.depthIn)));
        const thickest = Math.max(measurements[2], ...this.results.map((result) => Number(result.dataset.thicknessIn)));
        const topScale = Math.min(320 / widest, 215 / deepest);
        const sideScale = Math.min(320 / widest, 50 / thickest);
        const rectangle = (width, height, scale, centerY) => ({ x: 200 - width * scale / 2, y: centerY - height * scale / 2, width: width * scale, height: height * scale });
        return {
          device: rectangle(measurements[0], measurements[1], topScale, 137.5),
          sleeve: rectangle(limits[0], limits[1], topScale, 137.5),
          sideDevice: rectangle(measurements[0], measurements[2], sideScale, 40),
          sideSleeve: rectangle(limits[0], limits[2], sideScale, 40),
        };
      }

      updateComparisonVisual(result, measurements) {
        if (!this.comparisonVisual) return;
        const limits = ['widthIn', 'depthIn', 'thicknessIn'].map((key) => Number(result.dataset[key]));
        const geometry = this.comparisonGeometry(measurements, limits);
        this.comparisonVisual.dataset.state = result.dataset.state;
        this.comparisonVisual.dataset.size = result.dataset.variantSizeMatch;
        for (const [key, rectangle] of Object.entries(geometry)) {
          this.comparisonVisual.querySelectorAll(`[data-article-guide-plot="${key}"]`).forEach((shape) => {
            for (const [attribute, value] of Object.entries(rectangle)) shape.setAttribute(attribute, value.toFixed(4));
          });
        }
        this.dimensionRows.forEach((row, index) => {
          const gap = limits[index] - measurements[index];
          const within = gap > 0.000000001;
          row.dataset.state = within ? 'within' : 'outside';
          row.querySelector('[data-article-guide-yours]').textContent = `${this.displayDimension(measurements[index])} ${this.unit}`;
          row.querySelector('[data-article-guide-limit]').textContent = `${this.displayDimension(limits[index])} ${this.unit}`;
          row.querySelector('[data-article-guide-gap]').textContent = Math.abs(gap) < 0.000000001 ? 'No extra room' : `${this.formatClearance(Math.abs(gap))} ${this.unit} ${within ? 'room' : 'over'}`;
        });
      }

      onMeasurementKeyDown(event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const input = event.currentTarget;
        if (!input || input.validity?.badInput || !Number.isFinite(Number(input.value)) || Number(input.value) <= 0) {
          this.validationRequested = true;
          this.updateResults();
          return;
        }
        const next = this.inputs[this.inputs.indexOf(input) + 1];
        if (next) next.focus();
        else this.onCheck();
      }

      onSizeAction(event) {
        const size = event.currentTarget?.dataset.articleGuideVariantSize || '';
        if (!size) return;
        const eventDocument = this.ownerDocument || (typeof document !== 'undefined' ? document : null);
        const workplaceCard = eventDocument?.querySelector?.(
          'article-guide-product-card[data-product-handle="workplace-laptop-sleeve"]',
        );
        const selected = workplaceCard?.selectVariantBySize?.(size) || false;
        if (selected) workplaceCard.variantSelect?.focus?.({ preventScroll: true });

        publishArticleGuideEvent(this, 'fit_product_route', {
          articleHandle: this.dataset.articleHandle || '',
          productHandle: 'workplace-laptop-sleeve',
          selectedSize: size,
          variantSelected: selected,
        });
      }

      onClear() {
        this.hasChecked = false;
        this.validationRequested = false;
        this.measurementsInches.clear();
        this.inputs.forEach((input) => {
          input.value = '';
          input.removeAttribute('aria-invalid');
          const error = this.fieldErrors.get(input.dataset.articleGuideFitInput);
          if (error) error.hidden = true;
        });
        this.updateResults();
        this.inputs[0]?.focus();
        this.lastFitEventSignature = '';
        publishArticleGuideEvent(this, 'fit_clear', {
          articleHandle: this.dataset.articleHandle || '',
        });
      }

      scheduleFitCheckEvent(detail) {
        const signature = [
          detail.unit,
          detail.resultsWithinListedDimensions,
          detail.smallestEligibleSize || 'none',
        ].join(':');
        if (signature === this.lastFitEventSignature) return;

        if (this.fitEventTimer && typeof clearTimeout === 'function') clearTimeout(this.fitEventTimer);
        const publish = () => {
          this.fitEventTimer = null;
          if (signature === this.lastFitEventSignature) return;
          this.lastFitEventSignature = signature;
          publishArticleGuideEvent(this, 'fit_check', detail);
        };

        if (typeof setTimeout === 'function') this.fitEventTimer = setTimeout(publish, 450);
        else publish();
      }

      updateResults() {
        this.updateMeasurementVisual();
        const values = Object.fromEntries(
          this.inputs.map((input) => [input.dataset.articleGuideFitInput, Number(input.value)]),
        );
        const measurements = ['width', 'depth', 'thickness'].map((key) => values[key]);
        const complete = measurements.every((value) => Number.isFinite(value) && value > 0) && !this.inputs.some((input) => input.validity?.badInput);
        const showResults = complete && this.hasChecked;
        const hasAnyValue = this.inputs.some((input) => input.value.trim() !== '' || input.validity?.badInput);

        if (this.clearButton) this.clearButton.hidden = !hasAnyValue;
        if (this.resultGrid) this.resultGrid.hidden = !showResults;
        if (this.resultArea) this.resultArea.hidden = !showResults;
        if (!showResults && this.comparison) this.comparison.open = false;
        this.inputs.forEach((input) => {
          const entered = input.value.trim() !== '';
          const value = Number(input.value);
          const invalid = input.validity?.badInput || ((entered || this.validationRequested) && (!Number.isFinite(value) || value <= 0));
          if (invalid) input.setAttribute('aria-invalid', 'true');
          else input.removeAttribute('aria-invalid');
          const error = this.fieldErrors.get(input.dataset.articleGuideFitInput);
          if (error) error.hidden = !invalid;
        });

        if (!showResults) {
          if (this.fitEventTimer && typeof clearTimeout === 'function') clearTimeout(this.fitEventTimer);
          this.fitEventTimer = null;
          this.status.textContent = '';
          if (this.summaryDetail) this.summaryDetail.textContent = '';
          if (this.primaryAction) this.primaryAction.hidden = true;
          this.results.forEach((result) => {
            result.dataset.state = 'idle';
            result.querySelector('[data-article-guide-verdict]').textContent = 'Waiting for measurements';
            const detail = result.querySelector('[data-article-guide-result-detail]');
            const action = result.querySelector('[data-article-guide-size-action]');
            if (detail) detail.hidden = true;
            if (action) action.hidden = true;
          });
          return;
        }

        const measuredInches = ['width', 'depth', 'thickness'].map((key) => {
          const input = this.inputs.find((field) => field.dataset.articleGuideFitInput === key);
          const stored = this.measurementsInches.get(input);
          if (stored?.display === input.value && stored.unit === this.unit) return stored.inches;
          const inches = this.unitToInches(Number(input.value));
          this.measurementsInches.set(input, { inches, display: input.value, unit: this.unit });
          return inches;
        });
        const measurementKeys = ['width', 'depth', 'thickness'];
        const eligibleSizes = [];
        this.results.forEach((result) => {
          const limits = ['widthIn', 'depthIn', 'thicknessIn'].map((key) => Number(result.dataset[key]));
          const within = measuredInches.every((value, index) => value < limits[index] - 0.000000001);
          const verdict = result.querySelector('[data-article-guide-verdict]');
          const detail = result.querySelector('[data-article-guide-result-detail]');
          const action = result.querySelector('[data-article-guide-size-action]');
          result.dataset.state = within ? 'within' : 'outside';
          verdict.textContent = within ? 'Within all listed dimensions' : 'At or over a listed limit';
          if (action) action.hidden = !within;

          if (within) {
            const clearances = limits.map((limit, index) => limit - measuredInches[index]);
            const closestClearance = Math.min(...clearances);
            const closestIndex = clearances.indexOf(closestClearance);
            if (detail) {
              detail.textContent = `Closest listed clearance: ${measurementKeys[closestIndex]}, ${this.formatClearance(closestClearance)} ${this.unit}.`;
              detail.hidden = false;
            }
            eligibleSizes.push(result.dataset.variantSizeMatch || '');
            return;
          }

          const overages = limits
            .map((limit, index) => ({
              label: measurementKeys[index],
              overage: measuredInches[index] - limit,
            }))
            .filter(({ overage }) => overage >= -0.000000001);
          if (detail) {
            detail.textContent = overages
              .map(({ label, overage }) => {
                const capitalizedLabel = `${label[0].toUpperCase()}${label.slice(1)}`;
                if (Math.abs(overage) < 0.000001) {
                  return `${capitalizedLabel} meets this listed limit. Additional clearance is required.`;
                }
                return `${capitalizedLabel} is ${this.formatClearance(overage)} ${this.unit} over this listed interior.`;
              })
              .join(' ');
            detail.hidden = false;
          }
        });

        const withinCount = eligibleSizes.length;
        const size = this.previewSize || eligibleSizes.find(Boolean) || 'large';
        this.previewSize = size;
        const selectedResult = this.results.find((result) => result.dataset.variantSizeMatch === size);
        const selectedWithin = selectedResult.dataset.state === 'within';
        const sizeLabel = size === 'medium' ? 'Medium' : 'Large';
        this.previewChoices.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.articleGuidePreviewSize === size)));
        this.status.textContent = selectedWithin ? `${sizeLabel} has room for your measurements` : `${sizeLabel} is too close or too small`;
        if (this.summaryDetail) {
          if (selectedWithin) {
            this.summaryDetail.textContent = `All three measurements are smaller than the ${sizeLabel} interior. Confirm close measurements with us before ordering.`;
          } else {
            const otherSize = eligibleSizes.find((eligible) => eligible !== size);
            this.summaryDetail.textContent = selectedResult.querySelector('[data-article-guide-result-detail]').textContent + (otherSize ? ` Compare the ${otherSize === 'medium' ? 'Medium' : 'Large'} size above.` : ' Ask us about fit before choosing a sleeve.');
          }
        }
        this.updateComparisonVisual(selectedResult, measuredInches);
        if (this.primaryAction) {
          this.primaryAction.hidden = false;
          this.primaryAction.dataset.articleGuideVariantSize = selectedWithin ? size : '';
          this.primaryAction.href = selectedWithin ? '#article-laptop-guide-shop' : '/pages/contact';
          this.primaryAction.textContent = selectedWithin ? `View ${sizeLabel} Sleeve options ↓` : 'Ask Nordweg about fit';
        }
        this.scheduleFitCheckEvent({
          articleHandle: this.dataset.articleHandle || '',
          unit: this.unit,
          resultsWithinListedDimensions: withinCount,
          smallestEligibleSize: eligibleSizes.find(Boolean) || 'none',
        });
      }
    },
  );
}

const protectArticleGuideNewsletterControls = () => {
  if (typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') return;

  const newsletter = document.querySelector('form.footer__newsletter');
  if (!newsletter || newsletter.dataset.articleGuideObserved === 'true') return;
  newsletter.dataset.articleGuideObserved = 'true';
  const observer = new IntersectionObserver(
    (entries) => {
      document.body.classList.toggle(
        'article-guide-newsletter-visible',
        entries.some((entry) => entry.isIntersecting),
      );
    },
    { rootMargin: '0px 0px 64px 0px', threshold: 0 },
  );
  observer.observe(newsletter);
};

protectArticleGuideNewsletterControls();
